import { useState } from 'react'
import { Link } from 'react-router-dom'
import HandJointSelector from '../components/HandJointSelector'

// Mock data for design review
const MOCK_STUDY_ID = 'hand123'
const MOCK_SUBMISSION_ID = 'design-preview-123'

const STEP_CONFIG = {
  1: { type: 'pain_assessment', title: 'Pain Assessment' },
  2: { type: 'age', title: 'Age' },
  3: { type: 'height', title: 'Height' },
  4: { type: 'weight', title: 'Weight' },
  5: { type: 'left_hand_pain', title: 'Left Hand Pain' },
  6: { type: 'right_hand_pain', title: 'Right Hand Pain' },
  7: { type: 'left_hand_swelling', title: 'Left Hand Swelling' },
  8: { type: 'right_hand_swelling', title: 'Right Hand Swelling' },
  9: { type: 'photo_instructions', title: 'Photo Instructions' },
  
  // Photo steps (10-17)
  10: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_down_spread' },
  11: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_down_fist' },
  12: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_up_spread' },
  13: { type: 'photo', title: 'Left Hand Photo', hand: 'left', position: 'palm_up_fist' },
  14: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_down_spread' },
  15: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_down_fist' },
  16: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_up_spread' },
  17: { type: 'photo', title: 'Right Hand Photo', hand: 'right', position: 'palm_up_fist' },
  
  // Video steps (18-21)
  18: { type: 'video', title: 'Left Hand Video', hand: 'left', position: 'palm_down_movement' },
  19: { type: 'video', title: 'Left Hand Video', hand: 'left', position: 'palm_up_movement' },
  20: { type: 'video', title: 'Right Hand Video', hand: 'right', position: 'palm_down_movement' },
  21: { type: 'video', title: 'Right Hand Video', hand: 'right', position: 'palm_up_movement' },
  
  22: { type: 'thank_you', title: 'Thank You' }
} as const

// Mock answers for realistic preview
const MOCK_ANSWERS = {
  pain_score: 6,
  age: 35,
  height_value: 175,
  height_unit: 'cm',
  weight_value: 70,
  weight_unit: 'kg',
  left_hand_pain: ['thumb_base', 'index_mid'],
  right_hand_pain: [],
  left_hand_swelling: ['wrist'],
  right_hand_swelling: ['middle_base', 'ring_base']
}

export default function DesignReview() {
  const [step, setStep] = useState(1)
  const [answers, setAnswers] = useState(MOCK_ANSWERS)
  
  const progress = Math.round((step / 22) * 100)
  const currentStepConfig = STEP_CONFIG[step as keyof typeof STEP_CONFIG]
  
  const next = () => setStep(s => Math.min(22, s + 1))
  const prev = () => setStep(s => Math.max(1, s - 1))
  const jumpToStep = (targetStep: number) => setStep(targetStep)
  const update = (k: string, v: any) => setAnswers(a => ({ ...a, [k]: v }))

  const canProceed = true // Always allow progression in design mode

  return (
    <div className='min-h-screen p-4 bg-gradient-to-br from-slate-50 to-slate-100'>
      <div className='max-w-md mx-auto space-y-6'>
        {/* Design Mode Banner */}
        <div className='bg-blue-100 border border-blue-300 rounded-lg p-3 text-center'>
          <p className='text-blue-800 text-sm font-medium'>🎨 DESIGN REVIEW MODE</p>
          <p className='text-blue-600 text-xs'>No backend calls • Mock data • Step navigation enabled</p>
        </div>

        {/* Header with logo and progress */}
        <div className='bg-white rounded-3xl shadow-lg p-6 space-y-4'>
          <div className='flex items-center justify-center space-x-2'>
            <div className='w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center'>
              <div className='w-6 h-6 grid grid-cols-3 gap-0.5'>
                {Array.from({length: 9}).map((_, i) => (
                  <div key={i} className={`w-1 h-1 rounded-full ${i % 2 === 0 ? 'bg-white' : 'bg-white/60'}`} />
                ))}
              </div>
            </div>
            <h1 className='logo-text text-2xl'>Joint AI</h1>
          </div>
          <div className='w-full bg-slate-200 rounded-full h-2 overflow-hidden'>
            <div className='progress-bar h-2 transition-all duration-300' style={{ width: `${progress}%` }} />
          </div>
          <span className='text-xs text-slate-500 font-mono'>{MOCK_STUDY_ID}</span>
        </div>

        {/* Step Navigator */}
        <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
          <div className='flex items-center justify-between mb-3'>
            <span className='text-sm font-medium text-yellow-800'>Step Navigator</span>
            <span className='text-xs text-yellow-600'>Step {step} of 22</span>
          </div>
          <div className='grid grid-cols-11 gap-1 mb-3'>
            {Array.from({length: 22}).map((_, i) => (
              <button
                key={i + 1}
                onClick={() => jumpToStep(i + 1)}
                className={`w-6 h-6 text-xs rounded ${
                  step === i + 1 
                    ? 'bg-yellow-500 text-white' 
                    : 'bg-yellow-200 text-yellow-700 hover:bg-yellow-300'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div className='flex gap-2'>
            <button onClick={prev} disabled={step === 1} className='px-3 py-1 bg-yellow-500 text-white text-xs rounded disabled:opacity-50'>
              ← Prev
            </button>
            <button onClick={next} disabled={step === 22} className='px-3 py-1 bg-yellow-500 text-white text-xs rounded disabled:opacity-50'>
              Next →
            </button>
          </div>
        </div>

        {/* Main content card */}
        <div className='card p-8 space-y-6'>
          {/* Step content */}
          {(() => {
            if (!currentStepConfig) return <div>Invalid step</div>
            
            const stepType = currentStepConfig.type
            
            switch (stepType) {
              case 'pain_assessment':
                const painValue = answers.pain_score ?? 5
                return (
                  <div className='space-y-8 py-6'>
                    <div className='text-center space-y-4'>
                      <h3 className='text-xl font-semibold text-slate-800 leading-relaxed'>
                        Considering all of the ways your disease affects you,<br />
                        how well are you doing in the past week?
                      </h3>
                    </div>
                    
                    <div className='space-y-6'>
                      {/* Pain value indicator */}
                      <div className='text-center'>
                        <div className='inline-block bg-white border-2 border-slate-300 rounded-full w-12 h-12 flex items-center justify-center shadow-lg'>
                          <span className='text-xl font-bold text-slate-800'>{painValue}</span>
                        </div>
                      </div>
                      
                      {/* Gradient slider */}
                      <div className='relative px-4'>
                        <div className='gradient-bar w-full relative'>
                          <input 
                            type='range' 
                            min={1} 
                            max={10} 
                            value={painValue}
                            onChange={(e) => update('pain_score', Number(e.target.value))}
                            className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
                          />
                          {/* Slider thumb */}
                          <div 
                            className='absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-3 border-slate-400 rounded-full shadow-lg pointer-events-none'
                            style={{ left: `calc(${((painValue - 1) / 9) * 100}% - 12px)` }}
                          />
                        </div>
                      </div>
                      
                      {/* Scale labels */}
                      <div className='flex justify-between text-sm text-slate-600 px-4'>
                        <div className='text-center'>
                          <div className='font-semibold'>1</div>
                          <div className='text-xs'>1 = Best</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>2</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>3</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>4</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>5</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>6</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>7</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>8</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>9</div>
                        </div>
                        <div className='text-center'>
                          <div className='font-semibold'>10</div>
                          <div className='text-xs'>10 = Worst</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              
              case 'age':
                return (
                  <div className='space-y-6 py-6'>
                    <h3 className='text-xl font-semibold text-slate-800 text-center'>
                      What is your age?
                    </h3>
                    <div className='space-y-2'>
                      <input
                        type="number"
                        value={answers.age || ''}
                        onChange={(e) => update('age', parseInt(e.target.value) || undefined)}
                        placeholder="Enter your age"
                        className='input text-center text-lg'
                        min="1"
                        max="120"
                      />
                      <p className='text-sm text-slate-500 text-center'>years</p>
                    </div>
                  </div>
                )
              
              case 'height':
                const heightUnit = answers.height_unit || 'ft'
                return (
                  <div className='space-y-6 py-6'>
                    <h3 className='text-xl font-semibold text-slate-800 text-center'>
                      What is your height?
                    </h3>
                    <div className='space-y-4'>
                      <div className='flex justify-center space-x-2'>
                        <button 
                          onClick={() => update('height_unit', 'ft')}
                          className={`px-4 py-2 rounded-lg ${heightUnit === 'ft' ? 'bg-green-500 text-white' : 'bg-slate-200'}`}
                        >
                          Feet
                        </button>
                        <button 
                          onClick={() => update('height_unit', 'cm')}
                          className={`px-4 py-2 rounded-lg ${heightUnit === 'cm' ? 'bg-green-500 text-white' : 'bg-slate-200'}`}
                        >
                          Centimeters
                        </button>
                      </div>
                      <input
                        type="number"
                        value={answers.height_value || ''}
                        onChange={(e) => update('height_value', parseFloat(e.target.value) || undefined)}
                        placeholder={heightUnit === 'ft' ? '5.8' : '175'}
                        className='input text-center text-lg'
                        step={heightUnit === 'ft' ? '0.1' : '1'}
                      />
                      <p className='text-sm text-slate-500 text-center'>
                        {heightUnit === 'ft' ? 'feet (e.g., 5.8)' : 'centimeters'}
                      </p>
                    </div>
                  </div>
                )
                
              case 'weight':
                const weightUnit = answers.weight_unit || 'lbs'
                return (
                  <div className='space-y-6 py-6'>
                    <h3 className='text-xl font-semibold text-slate-800 text-center'>
                      What is your weight?
                    </h3>
                    <div className='space-y-4'>
                      <div className='flex justify-center space-x-2'>
                        <button 
                          onClick={() => update('weight_unit', 'lbs')}
                          className={`px-4 py-2 rounded-lg ${weightUnit === 'lbs' ? 'bg-green-500 text-white' : 'bg-slate-200'}`}
                        >
                          Pounds
                        </button>
                        <button 
                          onClick={() => update('weight_unit', 'kg')}
                          className={`px-4 py-2 rounded-lg ${weightUnit === 'kg' ? 'bg-green-500 text-white' : 'bg-slate-200'}`}
                        >
                          Kilograms
                        </button>
                      </div>
                      <input
                        type="number"
                        value={answers.weight_value || ''}
                        onChange={(e) => update('weight_value', parseFloat(e.target.value) || undefined)}
                        placeholder={weightUnit === 'lbs' ? '150' : '70'}
                        className='input text-center text-lg'
                        step={weightUnit === 'lbs' ? '1' : '0.1'}
                      />
                      <p className='text-sm text-slate-500 text-center'>
                        {weightUnit === 'lbs' ? 'pounds' : 'kilograms'}
                      </p>
                    </div>
                  </div>
                )
              
              case 'left_hand_pain':
              case 'right_hand_pain':
              case 'left_hand_swelling':
              case 'right_hand_swelling':
                const hand = stepType.includes('left') ? 'left' : 'right'
                const condition = stepType.includes('pain') ? 'pain' : 'swelling'
                
                return (
                  <div className='py-6'>
                    <HandJointSelector
                      hand={hand as 'left' | 'right'}
                      condition={condition as 'pain' | 'swelling'}
                      selectedJoints={answers[stepType] || []}
                      onSelectionChange={(joints) => update(stepType, joints)}
                    />
                    
                    {/* Skip button for no pain/swelling */}
                    <div className='text-center pt-6'>
                      <button 
                        onClick={() => update(stepType, [])}
                        className='text-slate-500 underline hover:text-slate-700'
                      >
                        No {condition === 'pain' ? 'pain' : 'swelling'} in this hand
                      </button>
                    </div>
                  </div>
                )
              
              case 'photo_instructions':
                return (
                  <div className='space-y-6 py-6 text-center'>
                    <h3 className='text-xl font-semibold text-slate-800'>
                      You'll now be asked to take photos and videos of your hands
                    </h3>
                    
                    <div className='space-y-4 text-left'>
                      <p>• Please remove jewelry if you can</p>
                      <p>• Please ensure there's good lighting and your hands are in focus when you take the photo</p>
                      <p>• You'll need access to your camera only to take these photos and videos of your hands</p>
                      <p>• We will not be accessing any of your other photos or files</p>
                    </div>
                    
                    <div className='flex justify-center space-x-4 pt-4'>
                      <div className='w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center'>📷</div>
                      <div className='w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center'>💡</div>
                      <div className='w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center'>📱</div>
                    </div>
                  </div>
                )
              
              case 'photo':
                const photoHand = currentStepConfig.hand
                const photoPosition = currentStepConfig.position
                
                return (
                  <div className='space-y-6'>
                    <h3 className='text-xl font-semibold text-slate-800 text-center'>
                      Please take a photo of your {photoHand} hand
                    </h3>
                    <p className='text-center text-slate-600'>
                      {photoPosition?.replace(/_/g, ' ')}
                    </p>
                    {/* Mock photo capture */}
                    <div className='space-y-3 text-center'>
                      <div className='bg-slate-200 rounded-lg p-8 border-2 border-dashed border-slate-400'>
                        <div className='text-slate-500 text-4xl mb-4'>📷</div>
                        <p className='text-slate-600'>Photo capture interface</p>
                        <p className='text-sm text-slate-500 mt-2'>{photoPosition?.replace(/_/g, ' ')}</p>
                      </div>
                      <button className='btn btn-primary w-full'>
                        Take Photo
                      </button>
                    </div>
                  </div>
                )
              
              case 'video':
                const videoHand = currentStepConfig.hand
                const videoPosition = currentStepConfig.position
                
                return (
                  <div className='space-y-6'>
                    <h3 className='text-xl font-semibold text-slate-800 text-center'>
                      Record a video of your {videoHand} hand
                    </h3>
                    <p className='text-center text-slate-600'>
                      Making a fist and then spreading out your fingers
                    </p>
                    {/* Mock video capture */}
                    <div className='space-y-3 text-center'>
                      <div className='bg-slate-200 rounded-lg p-8 border-2 border-dashed border-slate-400'>
                        <div className='text-slate-500 text-4xl mb-4'>🎥</div>
                        <p className='text-slate-600'>Video recording interface</p>
                        <p className='text-sm text-slate-500 mt-2'>60 second max duration</p>
                      </div>
                      <button className='btn btn-primary w-full'>
                        Start Recording
                      </button>
                    </div>
                  </div>
                )
              
              case 'thank_you':
                return (
                  <div className='space-y-6 py-6 text-center'>
                    <h3 className='text-2xl font-bold text-slate-800'>Thank You!</h3>
                    <p className='text-xl text-slate-600'>We received your submission</p>
                    <div className='text-green-500 text-6xl'>✓</div>
                    <Link to='/' className='btn btn-primary'>
                      Return to Home
                    </Link>
                  </div>
                )
              
              default:
                return <div>Unknown step type: {stepType}</div>
            }
          })()}

          {/* Navigation buttons */}
          <div className='flex gap-4 pt-4'>
            <button 
              className='btn btn-secondary flex-1' 
              onClick={prev} 
              disabled={step === 1}
            >
              Back
            </button>
            <button 
              className='btn btn-primary flex-1' 
              onClick={step === 22 ? undefined : next} 
              disabled={step === 22 || !canProceed}
            >
              {step === 22 ? 'Complete' : 'Next →'}
            </button>
          </div>
        </div>

        {/* Design mode footer */}
        <div className='text-center'>
          <Link to='/' className='text-slate-500 hover:text-slate-700 text-sm'>
            ← Back to Production App
          </Link>
        </div>
      </div>
    </div>
  )
}